/**
 * Crossfade the page into the next theme or language.
 * Language uses a slightly shorter fade than light/dark.
 */

type ViewTransitionHandle = {
  ready?: Promise<void>;
  finished?: Promise<void>;
  updateCallbackDone?: Promise<void>;
};

export type PageCrossfadeOptions = {
  /** A touch shorter than the theme crossfade. */
  soft?: boolean;
};

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

const isAdminPage = () => window.location.pathname.startsWith('/admin');

/** Resolves when the new page state is committed. `onReady` fires as the fade becomes visible. */
export function runWithPageCrossfade(
  apply: () => void | Promise<void>,
  onReady?: () => void,
  options?: PageCrossfadeOptions
): Promise<void> {
  const ready = () => onReady?.();
  const soft = options?.soft === true;

  const runApply = () => Promise.resolve(apply());

  if (prefersReducedMotion() || isAdminPage()) {
    return runApply().then(ready, ready);
  }

  const root = document.documentElement;

  const startViewTransition = (
    document as Document & {
      startViewTransition?: (update: () => void | Promise<void>) => ViewTransitionHandle;
    }
  ).startViewTransition?.bind(document);

  if (startViewTransition) {
    root.classList.add('locale-capturing');
    if (soft) root.classList.add('locale-crossfade');
    else root.classList.add('theme-mode-switching');
    const clearCapturing = () => root.classList.remove('locale-capturing');
    const clearCrossfade = () => root.classList.remove('locale-crossfade', 'theme-mode-switching', 'locale-capturing');

    let transition: ViewTransitionHandle;
    try {
      transition = startViewTransition(() => apply());
    } catch {
      clearCrossfade();
      return runApply().then(ready, ready);
    }

    const whenReady = transition?.ready;
    if (whenReady) {
      void whenReady.then(() => {
        clearCapturing();
        ready();
      }, () => {
        clearCapturing();
        ready();
      });
    } else {
      window.setTimeout(() => {
        clearCapturing();
        ready();
      }, 0);
    }

    const finished = transition?.finished;
    if (finished) void finished.then(clearCrossfade, clearCrossfade);
    else window.setTimeout(clearCrossfade, soft ? 360 : 420);

    const visualDone = transition?.finished ?? transition?.updateCallbackDone;
    if (visualDone) return visualDone.then(() => undefined, () => undefined);
    return Promise.resolve();
  }

  const fadeInMs = soft ? 220 : 320;
  const fadeOutMs = soft ? 240 : 340;
  const cover = root.classList.contains('dark') ? '#1f2937' : '#f9fafb';
  const overlay = document.createElement('div');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:10000',
    'pointer-events:none',
    `background:${cover}`,
    'opacity:0',
    `transition:opacity ${fadeInMs}ms ease`,
  ].join(';');
  document.body.appendChild(overlay);
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });

  return new Promise((resolve) => {
    window.setTimeout(() => {
      void runApply().then(() => {
        requestAnimationFrame(() => {
          overlay.style.opacity = '0';
        });
        window.setTimeout(() => {
          overlay.remove();
          ready();
          resolve();
        }, fadeOutMs);
      }, () => {
        overlay.remove();
        ready();
        resolve();
      });
    }, fadeInMs);
  });
}

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const nextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

/**
 * Ease a dimmer over the page until it fully hides the screen, apply the change,
 * then ease the dimmer away. The header stays above it so the loader can keep turning.
 */
export function runWithHeldContentFade(
  apply: () => void | Promise<void>,
  onReady?: () => void,
  options?: { soft?: boolean }
): Promise<void> {
  const finish = () => onReady?.();
  const soft = options?.soft === true;
  const inMs = soft ? 280 : 340;
  const outMs = soft ? 300 : 380;

  if (prefersReducedMotion() || isAdminPage()) {
    return Promise.resolve(apply()).then(finish, finish);
  }

  const dimmer = document.createElement('div');
  dimmer.setAttribute('aria-hidden', 'true');
  dimmer.className = soft ? 'page-dimmer page-dimmer-soft' : 'page-dimmer';
  const paint = getComputedStyle(document.body).backgroundColor;
  const opaque = paint !== '' && paint !== 'transparent' && paint !== 'rgba(0, 0, 0, 0)';
  dimmer.style.background = opaque
    ? paint
    : (document.documentElement.classList.contains('dark') ? '#1f2937' : '#f9fafb');
  document.body.appendChild(dimmer);

  const remove = () => dimmer.remove();

  return nextFrame()
    .then(() => nextFrame())
    .then(() => {
      dimmer.classList.add('page-dimmer-in');
      return wait(inMs);
    })
    .then(() => Promise.resolve(apply()))
    .then(
      async () => {
        await nextFrame();
        await nextFrame();
        finish();
        dimmer.classList.remove('page-dimmer-in');
        await wait(outMs);
        remove();
      },
      () => {
        remove();
        finish();
      }
    );
}
